import React, { useState, useMemo } from 'react';
import {FlowData, ProductInstance, SELFCARE_REPORT_FLOW_TYPE} from "../../../../util/types.ts";
import IconButton from "../../IconButton.tsx";
import IconCross from "../../../icons/IconCross.tsx";
import {TechValues} from "../../../../util/SelfcareErrorReportTypes.ts";

interface AttachFilesComponentProps {
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    product: ProductInstance,
    flow: SELFCARE_REPORT_FLOW_TYPE,
}

const MAX_FILE_SIZE = 52428800 // = 50 MB
const MAX_COLLECTIVE_FILE_SIZE = 104857600 // = 100 MB
const MAX_NUMBER_OF_FILES = 10
const FILE_TYPES = [
    {extension: "jpg",  prefix: "image/"},
    {extension: "jpeg", prefix: "image/"},
    {extension: "png",  prefix: "image/"},
    {extension: "webp", prefix: "image/"},
    {extension: "pdf",  prefix: "application/"},
    {extension: "mp4",  prefix: "video/"},
    {extension: "mov",  prefix: "video/"},
    {extension: "webm", prefix: "video/"},
];

const fileTypesText = FILE_TYPES.map((type) => `.${type.extension}`).join(", ").replace(/, ([^,]*)$/, " eller $1");

const validFileExtension = (filename: string): boolean => {
    const file_ext = filename.split(".").pop()?.toLowerCase();
    return FILE_TYPES.some((type) => type.extension === file_ext);
};

const fileInputAccept = FILE_TYPES.map((type) => `${type.prefix}${type.extension}`).join(", ");

const fileSizesTooBig: (file_sizes: number) => boolean = (file_sizes: number): boolean => {
    return file_sizes > MAX_COLLECTIVE_FILE_SIZE
}

const sumFileSizes: (file_list: File[]) => number = ( file_list: File[]): number => {
    if (file_list.length == 0){
        return 0;
    }
    return file_list
        .map((a) => a.size)
        .reduce(function (a: number, b: number): number {
            return a + b;
        })
}

export default function AttachFilesComponent({
    setFlowData,
    product,
    flow,
}: AttachFilesComponentProps) {
    const [errorFileWrongType , setErrorFileWrongType] = useState<string[]|null>(null);
    const [errorFileTooBig, setErrorFileTooBig] = useState<string[]|null>(null);
    const [errorTooManyFile, setErrorTooManyFiles] = useState<boolean>(false);
    const [errorSelectedFilesTooBig, setSelectedFilesTooBig] = useState<boolean>(false);
    const [files, setFiles] = useState<File[]>([]);

    const mapTech = (tech: ProductInstance): string => {
        const techTypeName = tech.product.att_statistics_technology || "";

        if ([TechValues.Fiber, TechValues.Coax].includes(techTypeName)) {
            return techTypeName;
        }

        return TechValues.Other;
    };

    const product_att = useMemo(() => mapTech(product), [product]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const target_files: FileList = e.target.files != null ? e.target.files : new FileList()
        const accepted_files: File[] = [];
        files.map( (f:File) => accepted_files.push(f) );

        if ( target_files.length > MAX_NUMBER_OF_FILES ) {
            setErrorTooManyFiles(true)
            return
        }
        setErrorFileWrongType(null)
        setErrorFileTooBig(null)
        setErrorTooManyFiles(false)
        setSelectedFilesTooBig(false)

        if (target_files.length > 0) {
            for (let index: number = 0; index < target_files.length; index++) {
                if ( !validFileExtension(target_files[index].name) ){
                    setErrorFileWrongType([target_files[index].name])
                    break;
                }
                if (target_files[index].size > MAX_FILE_SIZE) {
                    setErrorFileTooBig([target_files[index].name])
                    break;
                } else {
                    if (!accepted_files.find( (elm) => elm.name === target_files[index]?.name ) ) {
                        if ( accepted_files.length > MAX_NUMBER_OF_FILES ) {
                            setErrorTooManyFiles(true)
                            break
                        }
                        if( sumFileSizes( accepted_files ) > MAX_COLLECTIVE_FILE_SIZE - target_files[index].size ) {
                            setSelectedFilesTooBig(true)
                            break;
                        }
                        accepted_files.push(target_files[index])
                    }
                }
            }
            setFiles(accepted_files)
            setFlowData((prevFlowData) => ({
                ...prevFlowData,
                attachedFiles: accepted_files,
            }))
        }
    }

    const removeFile = (files: File[], file: File, index: number) => {
        if (files?.length == 0 ) {
            return
        }
        if (file.name != files[index].name){
            return;
        }

        const update_files: File[] = [];
        files.map((f: File, i: number) => {
            if (i != index) {
                update_files.push(f);
            }
        })

        if ( !fileSizesTooBig( sumFileSizes(update_files) ) ) {
            setSelectedFilesTooBig(false)
        } else {
            setSelectedFilesTooBig(true)
        }

        if ( update_files.length <= MAX_NUMBER_OF_FILES ) {
            setErrorTooManyFiles(false)
        }

        setFiles(
            update_files
        )
        setFlowData((prevFlowData) => ({
            ...prevFlowData,
            attachedFiles: update_files,
        }));
    }

    const showGeneralProductFlowOffline = () => {
        return <p>Vedhæft gerne et billede af, hvordan din router er opsat og lyser.</p>
    }

    const showFiberProductFlowOffline = () => {
        return <>
            <p>For at vi kan hjælpe dig bedst, kan du vedhæfte billeder af:</p>
            <ul className={'list list--bullet'}>
                <li><p>Hvordan din router er opsat og lyser.</p></li>
                <li><p>Hvordan din fiberboks er opsat og lyser.</p></li>
            </ul>
        </>
    }

    const showCoaxProductFlowOffline = () => {
        return <>
            <p>For at vi kan hjælpe dig bedst må du gerne vedhæfte billeder af:</p>
            <ul className={'list list--bullet'}>
                <li><p>Hvordan din router er opsat og lyser.</p></li>
                <li><p>Kabel-tv-stikket som din router sidder i.</p></li>
            </ul>
        </>
    }

    const showGeneralProductFlowUnstable = () => {
        return <p>Vedhæft gerne et billede af, hvordan din router er opsat og lyser, når du oplever udfordringer med internettet.</p>
    }

    const showGeneralProductFlowSpeed = () => {
        return <p>Vedhæft gerne et skærmbillede af en hastighedstest, du har lavet for nylig.</p>
    }

    const errorShowFileTooBig = () => {
        return <p className={'error-message'} >Filen {errorFileTooBig} fylder over 50 MB. Komprimér filen og prøv igen.</p>
    }

    const errorShowWrongFileType = () => {
        return <p className={'error-message'}>Filen {errorFileWrongType} er ikke af typen: .jpg, .png, .pdf, .mp4, .mov, .webp</p>
    }

    const errorShowTooManyFiles = () => {
        return <p className={'error-message'}>Du kan maks. vælge {MAX_NUMBER_OF_FILES} filer. Vi har vedhæftet de første {MAX_NUMBER_OF_FILES} filer, du valgte.</p>
    }

    const errorShowMaximumFileSizes = () => {
        return <p className={'error-message'}>Filerne må tilsammen maks. fylde 100 MB. Vi har vedhæftet de filer, der tilsammen fylder maks. 100 MB.</p>
    }

    const hasError = errorFileTooBig || errorFileWrongType || errorTooManyFile || errorSelectedFilesTooBig;

    return (
        <div className={`input-container${hasError ? ' error' : ''}`}>
            {(flow === SELFCARE_REPORT_FLOW_TYPE.OFFLINE && product_att === TechValues.Other) && showGeneralProductFlowOffline()}
            {(flow === SELFCARE_REPORT_FLOW_TYPE.OFFLINE && product_att === TechValues.Fiber) && showFiberProductFlowOffline()}
            {(flow === SELFCARE_REPORT_FLOW_TYPE.OFFLINE && product_att === TechValues.Coax) && showCoaxProductFlowOffline()}
            {flow === SELFCARE_REPORT_FLOW_TYPE.UNSTABLE && showGeneralProductFlowUnstable()}
            {flow === SELFCARE_REPORT_FLOW_TYPE.SPEED && showGeneralProductFlowSpeed()}

            <div className={'file-selector pb-half'}>
                <label htmlFor="file" className={'file-selector-label button button--montserrat teal'}>Vælg fil...</label>
                <input id="file"
                       type="file"
                       accept={fileInputAccept}
                       multiple
                       onChange={handleFileChange}/>

                {[...files].map((file, index) => (
                    <div key={index}  className={'file-name button button--montserrat gray with-icon'}>
                        <span className={'text'}>
                            {file.name}
                        </span>
                        <IconButton
                            onClick={ () => {
                                    removeFile(files, file, index)
                                }
                            }
                            className={'icon-button'}
                            component={IconCross}
                            aria-label={'Fjern fil'}
                        />
                    </div>
                ))}
            </div>
            {errorFileTooBig && errorShowFileTooBig()}
            {errorFileWrongType && errorShowWrongFileType()}
            {errorTooManyFile && errorShowTooManyFiles()}
            {errorSelectedFilesTooBig && errorShowMaximumFileSizes()}
            <div>
                <p className={'small'}>
                    Filen skal være {fileTypesText} og må ikke være større end {MAX_FILE_SIZE / (1024 * 1024)} MB.
                    Du kan vedhæfte op til {MAX_NUMBER_OF_FILES} filer som må fylde maks. {MAX_COLLECTIVE_FILE_SIZE / (1024 * 1024)} MB til sammen.
                </p>
            </div>
        </div>
    );
}
